/** Client-side date scripting for API param previews (mirrors server apiParamScripting). */

function formatUtcDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function addUtcDays(date, deltaDays) {
  const d = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  ));
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d;
}

export function resolveDateScript(raw, referenceDate = new Date()) {
  const text = String(raw ?? '').trim();
  if (!text) return text;

  const lower = text.toLowerCase();
  if (lower === 'now' || lower === 'today') {
    return formatUtcDate(referenceDate);
  }

  const daysAgo = /^(\d+)\s+days?\s+ago$/i.exec(text);
  if (daysAgo) {
    const n = Number.parseInt(daysAgo[1], 10);
    if (Number.isFinite(n) && n >= 0) {
      return formatUtcDate(addUtcDays(referenceDate, -n));
    }
  }

  const isoDate = /^(\d{4}-\d{2}-\d{2})$/.exec(text);
  if (isoDate) return isoDate[1];

  const isoDateTime = /^(\d{4}-\d{2}-\d{2})[tT\s].+$/.exec(text);
  if (isoDateTime) return isoDateTime[1];

  return text;
}

function tryParseJsonValue(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return null;
  try {
    return JSON.parse(trimmed);
  } catch (_) {
    return null;
  }
}

export function applyScriptToValue(value, referenceDate = new Date()) {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    const scripted = resolveDateScript(value, referenceDate);
    const parsed = tryParseJsonValue(scripted);
    if (parsed !== null) return applyScriptToValue(parsed, referenceDate);
    return scripted;
  }

  if (Array.isArray(value)) {
    return value.map((item) => applyScriptToValue(item, referenceDate));
  }

  if (typeof value === 'object') {
    const out = {};
    for (const [key, nested] of Object.entries(value)) {
      out[key] = applyScriptToValue(nested, referenceDate);
    }
    return out;
  }

  return value;
}

export function resolveScriptedParamValue(raw, referenceDate = new Date()) {
  return applyScriptToValue(raw, referenceDate);
}

export function coerceJsonScalar(value) {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (trimmed === '') return value;
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;

  if (/^-?\d+$/.test(trimmed)) {
    const n = Number.parseInt(trimmed, 10);
    if (Number.isSafeInteger(n)) return n;
  }

  if (/^-?\d+\.\d+$/.test(trimmed)) {
    const n = Number.parseFloat(trimmed);
    if (Number.isFinite(n)) return n;
  }

  return value;
}

export function coerceBodyJsonValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => coerceBodyJsonValue(item));
  }

  if (value && typeof value === 'object') {
    const out = {};
    for (const [key, nested] of Object.entries(value)) {
      out[key] = coerceBodyJsonValue(nested);
    }
    return out;
  }

  return coerceJsonScalar(value);
}
